import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FileText, Download, QrCode, CheckCircle, Clock, Eye,
  Plus, RefreshCw, Award, Users, Send, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DeliberationPage = () => {
  const { user } = useAuth();
  const [deliberations, setDeliberations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedDelib, setSelectedDelib] = useState(null);
  const [pdfData, setPdfData] = useState(null);

  const isAdmin = ['super_admin', 'rector', 'vice_rector_academic', 'sg_academic', 'dean', 'department_head'].includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/deliberations`);
      setDeliberations(res.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePublish = async (id) => {
    try {
      await axios.put(`${API_URL}/api/deliberations/${id}/publish`);
      toast.success('Deliberation publiee');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDownloadPdf = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/deliberations/${id}/pdf`);
      setPdfData(res.data);
      // Auto download
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${res.data.pdf_base64}`;
      link.download = res.data.filename || 'PV_Deliberation.pdf';
      link.click();
      toast.success('PDF telecharge');
    } catch (err) { toast.error('Erreur generation PDF'); }
  };

  if (loading) return <LoadingSkel />;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="deliberation-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Deliberations</h1>
          <p className="text-sm text-gray-500 mt-1">Proces-verbaux et releves de notes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" />Actualiser</Button>
          {isAdmin && (
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-delib-btn">
              <Plus className="w-4 h-4 mr-2" />Nouvelle deliberation
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><FileText className="w-5 h-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold">{deliberations.length}</p><p className="text-xs text-gray-500">Total</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
          <div><p className="text-2xl font-bold">{deliberations.filter(d => d.is_published).length}</p><p className="text-xs text-gray-500">Publiees</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
          <div><p className="text-2xl font-bold">{deliberations.filter(d => !d.is_published).length}</p><p className="text-xs text-gray-500">En attente</p></div>
        </CardContent></Card>
      </div>

      {/* Deliberations List */}
      {deliberations.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>Aucune deliberation creee.</p>
          {isAdmin && <p className="text-xs mt-1">Cliquez sur "Nouvelle deliberation" pour commencer.</p>}
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {deliberations.map(delib => (
            <Card key={delib.id} className="hover:shadow-sm transition-shadow" data-testid={`delib-card-${delib.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">Annee: {delib.academic_year}</span>
                      <Badge variant="outline" className="text-xs">Session {delib.session}</Badge>
                      {delib.is_published ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Publiee</Badge>
                      ) : (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs"><Clock className="w-3 h-3 mr-1" />Brouillon</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span><Users className="w-3 h-3 inline mr-1" />{delib.results?.length || 0} etudiant(s)</span>
                      {delib.results && (
                        <>
                          <span className="text-emerald-600"><Award className="w-3 h-3 inline mr-1" />{delib.results.filter(r => r.decision === 'Admis').length} Admis</span>
                          <span className="text-red-600">{delib.results.filter(r => r.decision === 'Ajourne').length} Ajournes</span>
                        </>
                      )}
                      <span>{new Date(delib.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSelectedDelib(delib)} data-testid={`view-delib-${delib.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" />Voir
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(delib.id)} data-testid={`pdf-delib-${delib.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1" />PDF
                    </Button>
                    {isAdmin && !delib.is_published && (
                      <Button size="sm" onClick={() => handlePublish(delib.id)} data-testid={`publish-delib-${delib.id}`}>
                        <Send className="w-3.5 h-3.5 mr-1" />Publier
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <CreateDelibDialog open={showCreate} onOpenChange={setShowCreate} onSuccess={fetchData} />

      {/* Detail Dialog */}
      {selectedDelib && (
        <DelibDetailDialog delib={selectedDelib} onClose={() => setSelectedDelib(null)} pdfData={pdfData} />
      )}
    </div>
  );
};

const CreateDelibDialog = ({ open, onOpenChange, onSuccess }) => {
  const [promotionId, setPromotionId] = useState('');
  const [academicYear, setAcademicYear] = useState('2025-2026');
  const [session, setSession] = useState('1');
  const [juryText, setJuryText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!promotionId) { toast.error('ID de promotion requis'); return; }
    setLoading(true);
    try {
      const jury = juryText.split(',').map(j => j.trim()).filter(Boolean);
      await axios.post(`${API_URL}/api/deliberations`, {
        promotion_id: promotionId,
        academic_year: academicYear,
        session: parseInt(session),
        jury_members: jury
      });
      toast.success('Deliberation creee avec les resultats calcules');
      onOpenChange(false);
      onSuccess?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Nouvelle deliberation</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>ID Promotion</Label>
            <Input value={promotionId} onChange={e => setPromotionId(e.target.value)}
              placeholder="ID de la promotion" required data-testid="delib-promotion-id" />
            <p className="text-xs text-gray-500 mt-1">Copiez l'ID depuis la gestion des promotions</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Annee academique</Label>
              <Input value={academicYear} onChange={e => setAcademicYear(e.target.value)} required />
            </div>
            <div>
              <Label>Session</Label>
              <Input type="number" value={session} onChange={e => setSession(e.target.value)} min="1" max="3" required />
            </div>
          </div>
          <div>
            <Label>Membres du jury (separes par virgule)</Label>
            <Input value={juryText} onChange={e => setJuryText(e.target.value)}
              placeholder="Prof. Mukendi, Prof. Kabila, Dr. Tshisekedi" />
          </div>
          <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
            <p className="text-xs text-blue-700">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              Les moyennes seront calculees automatiquement a partir des notes saisies dans le systeme.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={loading} data-testid="delib-create-submit">
              {loading ? 'Calcul en cours...' : 'Generer PV'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DelibDetailDialog = ({ delib, onClose, pdfData }) => {
  const results = delib.results || [];
  const admis = results.filter(r => r.decision === 'Admis');
  const reserve = results.filter(r => r.decision === 'Admis sous reserve');
  const ajourne = results.filter(r => r.decision === 'Ajourne');

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>PV de Deliberation - {delib.academic_year} (Session {delib.session})</DialogTitle>
        </DialogHeader>
        <div className="space-y-4" data-testid="delib-detail">
          {/* Summary */}
          <div className="grid grid-cols-4 gap-2">
            <div className="p-2 bg-gray-50 rounded text-center">
              <p className="text-lg font-bold">{results.length}</p><p className="text-[10px] text-gray-500">Total</p>
            </div>
            <div className="p-2 bg-emerald-50 rounded text-center">
              <p className="text-lg font-bold text-emerald-700">{admis.length}</p><p className="text-[10px] text-emerald-600">Admis</p>
            </div>
            <div className="p-2 bg-amber-50 rounded text-center">
              <p className="text-lg font-bold text-amber-700">{reserve.length}</p><p className="text-[10px] text-amber-600">Sous reserve</p>
            </div>
            <div className="p-2 bg-red-50 rounded text-center">
              <p className="text-lg font-bold text-red-700">{ajourne.length}</p><p className="text-[10px] text-red-600">Ajournes</p>
            </div>
          </div>

          {delib.jury_members?.length > 0 && (
            <p className="text-xs text-gray-500">Jury: {delib.jury_members.join(', ')}</p>
          )}

          {/* QR Code */}
          {pdfData?.qr_code_base64 && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border">
              <img src={`data:image/png;base64,${pdfData.qr_code_base64}`} alt="QR" className="w-16 h-16" />
              <div>
                <p className="text-xs font-medium">Code QR de verification</p>
                <p className="text-[10px] text-gray-500">{pdfData.qr_data}</p>
              </div>
            </div>
          )}

          {/* Results Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Matricule</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead className="text-center">Moyenne</TableHead>
                  <TableHead className="text-center">Credits</TableHead>
                  <TableHead className="text-center">Decision</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((r, i) => (
                  <TableRow key={r.student_id}>
                    <TableCell className="text-xs">{i + 1}</TableCell>
                    <TableCell className="text-xs font-mono">{r.matricule}</TableCell>
                    <TableCell className="text-sm">{r.student_name}</TableCell>
                    <TableCell className="text-center font-mono font-semibold">{r.average?.toFixed(2)}/20</TableCell>
                    <TableCell className="text-center text-xs">{r.total_credits}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={`text-xs ${
                        r.decision === 'Admis' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        r.decision === 'Admis sous reserve' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-red-50 text-red-700 border-red-200'
                      }`}>{r.decision}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Button onClick={onClose} className="w-full" variant="outline">Fermer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LoadingSkel = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-48" />
    <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-gray-200 rounded" />)}</div>
    <div className="h-64 bg-gray-200 rounded" />
  </div>
);

export default DeliberationPage;
